import { humanize, labelFor, plural, stepReversibilityLabel, strategyLabel, workflowLabel } from '../labels';
import React, { useState, useEffect, useId, useRef } from 'react';
import { ConfirmDialog, SkeletonPage, StatusBadge } from './ui';
import { Callout, Checkbox, Field, Icon, LogPanel, SectionHeader, Select, StepList, Textarea } from './primitives';
import { StageList } from './deploy/StageList';
import { StepError } from './deploy/StepError';
import type { LogLine, StepItem } from './primitives';
import {
  ExecutionRun,
  ExecutionStep,
  DeploymentStage,
  ExecutionLog,
  ExecutionCheckpoint,
  ExecutionControls,
  getExecution,
  startExecution,
  pauseExecution,
  resumeExecution,
  cancelExecution,
  retryExecutionStep,
  resolveExecution,
  getExecutionLogs,
  type ExecutionResolution,
  type FailureInterpretation,
  type StepCompensationOutcome,
} from '../api';
import { FailureInterpretationPanel } from './FailureInterpretationPanel';
import type { SectionPath } from './project/operationalHealth';
import { RunTiming, StepTime, timingOf, timingSentence, useNow } from './runTiming';

/** The outcome the engine recorded under result.compensation after a failure, if any. */
function compensationOutcome(step: ExecutionStep): StepCompensationOutcome | null {
  const raw = step.result?.compensation;
  if (!raw || typeof raw !== 'object' || typeof raw.status !== 'string') return null;
  return raw as StepCompensationOutcome;
}

/** The engine writes both "warn" and "warning"; both are one level here. */
function logLevel(level: string): string {
  const normalized = level.toLowerCase();
  return normalized === 'warn' ? 'warning' : normalized;
}

interface ExecutionRunViewProps {
  executionId: string;
  onBack?: () => void;
  /**
   * Called once each time the run reaches an end (completed, failed or
   * cancelled), also when it had already ended when opened. The page
   * around it re-reads what the run changed — a finished setup makes the
   * environment deployable — whichever way the person leaves this view;
   * only "Back" used to refresh it.
   */
  onSettled?: (status: string) => void;
  /** Turns a failure interpretation's targets into links (the project's sections). */
  sectionPath?: SectionPath;
}

const settledStatuses = new Set(['completed', 'failed', 'cancelled']);

export const ExecutionRunView: React.FC<ExecutionRunViewProps> = ({ executionId, onBack, onSettled, sectionPath }) => {
  const [execution, setExecution] = useState<ExecutionRun | null>(null);
  const [interpretation, setInterpretation] = useState<FailureInterpretation | null>(null);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [stages, setStages] = useState<DeploymentStage[]>([]);
  const [checkpoints, setCheckpoints] = useState<ExecutionCheckpoint[]>([]);
  const [controls, setControls] = useState<ExecutionControls | null>(null);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [resolving, setResolving] = useState<ExecutionResolution | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [logFilter, setLogFilter] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const headingId = useId();
  const stepsHeadingId = useId();
  const checkpointsHeadingId = useId();
  const logRef = useRef<HTMLDivElement>(null);

  // Tell the page once per end state, not on every poll of a finished run.
  const settledRef = useRef<string | null>(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const runStatus = execution?.id === executionId ? execution.status : null;
  // Live counters tick only while something runs.
  const now = useNow(Boolean(execution && (execution.status === 'running' || execution.status === 'preflighting' || steps.some((st) => st.status === 'running'))));
  useEffect(() => {
    if (!runStatus) return;
    if (!settledStatuses.has(runStatus)) {
      settledRef.current = null;
      return;
    }
    if (settledRef.current === runStatus) return;
    settledRef.current = runStatus;
    onSettledRef.current?.(runStatus);
  }, [runStatus]);

  const fetchState = async () => {
    try {
      const data = await getExecution(executionId);
      setExecution(data.execution);
      setInterpretation(data.interpretation ?? null);
      setSteps(data.steps ?? []);
      setStages(data.stages ?? []);
      setCheckpoints(data.checkpoints ?? []);
      setControls(data.controls);

      const logsData = await getExecutionLogs(executionId, 500);
      setLogs(logsData.logs || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load execution details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
    const interval = setInterval(() => {
      fetchState();
    }, 2000);
    return () => clearInterval(interval);
  }, [executionId]);

  // LogPanel owns its scrolling body; follow the newest line in it.
  useEffect(() => {
    const body = logRef.current?.querySelector<HTMLElement>('.log-panel__body');
    if (autoScroll && body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleAction = async (actionFn: () => Promise<any>) => {
    setActionPending(true);
    try {
      await actionFn();
      await fetchState();
    } catch (err: any) {
      setError(err?.message || 'Action failed');
    } finally {
      setActionPending(false);
    }
  };

  if (loading && !execution) {
    return <SkeletonPage rows={4} />;
  }

  if (error && !execution) {
    return (
      <Callout
        tone="danger"
        title="This run could not be loaded"
        actions={
          onBack ? (
            <button type="button" className="button button--secondary button--small" onClick={onBack}>
              Go back
            </button>
          ) : undefined
        }
      >
        {error}
      </Callout>
    );
  }

  if (!execution) return null;

  const filteredLogs = logs.filter((l) => {
    if (selectedStepId && l.step_id !== selectedStepId) return false;
    if (logFilter === 'all') return true;
    return logLevel(l.level) === logFilter.toLowerCase();
  });

  const logLines: LogLine[] = filteredLogs.map((log) => ({
    key: log.id,
    at: new Date(log.timestamp).toLocaleTimeString(),
    level: logLevel(log.level),
    source: log.source || undefined,
    text: log.message,
  }));

  const stepItems: StepItem[] = steps.map((st) => {
    const outcome = compensationOutcome(st);
    const verb = outcome
      ? outcome.status === 'succeeded'
        ? 'Undone after the failure'
        : outcome.status === 'failed'
          ? 'Undo failed'
          : 'Undo skipped'
      : '';
    return {
      key: st.id,
      title: st.title,
      description: st.description,
      status: { kind: 'execution', value: st.status },
      meta: <StepTime step={st} now={now} />,
      error: st.error_message ? <StepError message={st.error_message} /> : undefined,
      details: (
        <>
          <div className="type-meta" data-testid="step-timing-detail">{timingSentence(timingOf(st, now))}{st.attempt_count > 1 ? ` · ${st.attempt_count} attempts` : ''}</div>
          <div className="type-meta">
            {labelFor(stepReversibilityLabel, st.reversibility)} · <code>{st.command_name}</code>
          </div>
          {outcome ? (
            <div>
              <StatusBadge kind="execution" value={outcome.status} label={verb} /> {outcome.title ?? outcome.command}
              {outcome.error ? ` — ${outcome.error}` : ''}
            </div>
          ) : null}
        </>
      ),
      open: st.status === 'failed' || outcome !== null,
      testId: `run-step-${st.step_key}`,
    };
  });
  const stepItemByKey = new Map(steps.map((st, index) => [st.step_key, stepItems[index]]));

  const succeeded = steps.filter((s) => s.status === 'succeeded').length;
  const strategy = execution.plan_snapshot?.strategy;
  const hasActions =
    controls?.can_start || controls?.can_pause || controls?.can_resume || (controls?.can_retry && controls.retryable_step_id) || controls?.can_cancel;

  return (
    <div className="project-grid">
      <section className="panel project-grid__col--12" aria-labelledby={headingId}>
        {onBack ? (
          <div>
            <button type="button" className="button button--secondary button--small" onClick={onBack} aria-label="Back to project">
              <Icon name="back" size={16} />
              Back
            </button>
          </div>
        ) : null}
        <div className="section-header">
          <div className="section-header__text">
            <h2 id={headingId} className="section-header__title">
              {workflowLabel[execution.workflow_type] ?? humanize(execution.workflow_type)} run <code>#{execution.id.slice(0, 8)}</code>
            </h2>
            <div className="page-header__meta">
              <StatusBadge kind="execution" value={execution.status} />
              <RunTiming run={execution} now={now} />
              <span>
                {/* Only what the run has: a backup run has no plan and no strategy, so "Plan v0 · ·" (UX-12). */}
                {[
                  execution.mode === 'rehearsal' ? 'Rehearsal' : null,
                  execution.plan_version ? `Plan v${execution.plan_version}` : null,
                  strategy ? strategyLabel[strategy as keyof typeof strategyLabel] ?? humanize(strategy) : null,
                  `started by ${execution.created_by}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            {/* Why an action is not offered, e.g. a rolled-back run cannot be retried (RES-004). */}
            {controls?.reason && !controls.can_resolve ? (
              <p className="section-header__desc" data-testid="run-controls-reason">
                {controls.reason}
              </p>
            ) : null}
          </div>

          {hasActions ? (
            <div className="section-header__actions">
              {controls?.can_start && (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => handleAction(() => startExecution(executionId))}
                  disabled={actionPending}
                >
                  Start run
                </button>
              )}

              {controls?.can_pause && (
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => handleAction(() => pauseExecution(executionId))}
                  disabled={actionPending}
                >
                  Pause
                </button>
              )}

              {controls?.can_resume && (
                <button
                  type="button"
                  className={`button ${controls.can_start ? 'button--secondary' : 'button--primary'}`}
                  onClick={() => handleAction(() => resumeExecution(executionId))}
                  disabled={actionPending}
                >
                  Resume
                </button>
              )}

              {controls?.can_retry && controls.retryable_step_id && (
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => handleAction(() => retryExecutionStep(executionId, controls.retryable_step_id!))}
                  disabled={actionPending}
                >
                  Retry step
                </button>
              )}

              {controls?.can_cancel && (
                <button
                  type="button"
                  className="button button--danger-quiet"
                  onClick={() => handleAction(() => cancelExecution(executionId))}
                  disabled={actionPending}
                >
                  Cancel
                </button>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {controls?.can_resolve ? (
        <div className="project-grid__col--12">
          <Callout
            tone="warning"
            title="This run needs you"
            testId="run-needs-operator"
            actions={
              <>
                <button type="button" className="button button--secondary" onClick={() => setResolving('succeeded')} disabled={actionPending}>
                  Mark done
                </button>
                <button type="button" className="button button--secondary" onClick={() => setResolving('rerun')} disabled={actionPending}>
                  Run again
                </button>
                <button type="button" className="button button--danger-quiet" onClick={() => setResolving('failed')} disabled={actionPending}>
                  Mark failed
                </button>
              </>
            }
          >
            {controls.reason} The environment stays locked until you decide.
          </Callout>
          <ConfirmDialog
            open={resolving !== null}
            title={resolutionCopy[resolving ?? 'succeeded'].title}
            description={resolutionCopy[resolving ?? 'succeeded'].description}
            confirmLabel={resolutionCopy[resolving ?? 'succeeded'].confirm}
            danger={resolving !== 'succeeded'}
            busy={actionPending}
            onCancel={() => setResolving(null)}
            onConfirm={() => {
              const chosen = resolving;
              if (!chosen) return;
              void handleAction(() => resolveExecution(executionId, chosen, resolutionNote)).then(() => {
                setResolving(null);
                setResolutionNote('');
              });
            }}
          >
            <Field label="What you found on the server (optional)">
              <Textarea rows={3} value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} />
            </Field>
          </ConfirmDialog>
        </div>
      ) : null}

      {interpretation ? (
        <div className="project-grid__col--12">
          <FailureInterpretationPanel
            interpretation={interpretation}
            sectionPath={sectionPath}
            onShowStep={(key) => {
              const failed = steps.find((step) => step.step_key === key);
              if (failed) setSelectedStepId(failed.id);
              logRef.current?.scrollIntoView({ block: 'start' });
            }}
          />
        </div>
      ) : null}

      {error ? (
        <Callout
          tone="danger"
          className="project-grid__col--12"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => setError(null)}>
              Dismiss
            </button>
          }
        >
          {error}
        </Callout>
      ) : null}

      <section className="panel project-grid__col--6" aria-labelledby={stepsHeadingId}>
        <SectionHeader
          level={3}
          id={stepsHeadingId}
          title={stages.length > 0 ? 'Stages' : 'Steps'}
          description={
            steps.length > 0
              ? stages.length > 0
                ? `${plural(stages.length, 'stage')} · ${succeeded}/${plural(steps.length, 'step')} done`
                : `${succeeded}/${steps.length} done`
              : undefined
          }
        />
        {steps.length === 0 ? (
          <p className="panel__note muted">No steps yet.</p>
        ) : stages.length > 0 ? (
          <StageList stages={stages} stepItem={(key) => stepItemByKey.get(key)} now={now} label="Stages" testIdPrefix="run-stage" />
        ) : (
          <StepList steps={stepItems} label="Steps" />
        )}
      </section>

      <div className="project-grid__col--6" ref={logRef}>
        <LogPanel
          title="Log"
          lines={logLines}
          empty="No logs recorded yet."
          live={execution.status === 'running'}
          testId="run-log"
          toolbar={
            <>
              {steps.length > 0 ? (
                <Select aria-label="Step" value={selectedStepId ?? ''} onChange={(e) => setSelectedStepId(e.target.value || null)}>
                  <option value="">All steps</option>
                  {steps.map((st, index) => (
                    <option key={st.id} value={st.id}>
                      {index + 1}. {st.title}
                    </option>
                  ))}
                </Select>
              ) : null}
              <Select aria-label="Log level" value={logFilter} onChange={(e) => setLogFilter(e.target.value)}>
                <option value="all">All levels</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="debug">Debug</option>
              </Select>
              <Checkbox checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} label="Auto-scroll" />
            </>
          }
        />
      </div>

      {checkpoints.length > 0 && (
        <section className="panel project-grid__col--12" aria-labelledby={checkpointsHeadingId}>
          <SectionHeader level={3} id={checkpointsHeadingId} title={`Safety checkpoints (${checkpoints.length})`} />
          <ul>
            {checkpoints.map((cp) => (
              <li key={cp.id}>
                <code>{cp.name}</code> <span className="type-meta">Created {new Date(cp.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

/** What each resolution of an unknown step outcome does, in the operator's words. */
const resolutionCopy: Record<ExecutionResolution, { title: string; description: string; confirm: string }> = {
  succeeded: {
    title: 'Mark the step done?',
    description:
      "Only if you checked the server and the step's work finished. The run continues with the next step; this decision is recorded with your name.",
    confirm: 'Mark done',
  },
  rerun: {
    title: 'Run the step again?',
    description:
      'Only if you checked that the step is not still running on the server: two copies running at once can damage the store. It runs again as a new attempt.',
    confirm: 'Run again',
  },
  failed: {
    title: 'Mark the step failed?',
    description:
      'What earlier steps changed is undone where it can be, and the run ends failed. Use this when the step did not complete.',
    confirm: 'Mark failed',
  },
};
